/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Courses Component
 * 
 * Copyconst Courses = () => {
    // Don't auto-refresh notifications on courses page
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);(c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/pages/Courses.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Layout from '../Layout/Layout';
import CourseCard from '../components/CourseCard';
import api from '../utils/apiClient';
import { handleApiError } from '../utils/errorHandler';

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.dangerLight};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PageContainer = styled.div`
  display: block;
  
  @media (max-width: 1024px) {
    gap: ${({ theme }) => theme.spacing[6]};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Courses = () => {
    // Don't auto-refresh notifications on courses page
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalCourses: 0,
        freeCourses: 0,
        paidCourses: 0,
        enrolledCourses: 0
    });

  // Proprietary ownership marker - Srivatsan Sk
  React.useEffect(() => {
    if (!window.__CHIEF_MODULES) window.__CHIEF_MODULES = {};
    window.__CHIEF_MODULES.courses = {
      author: 'Srivatsan Sk',
      copyright: '© 2025 Srivatsan Sk',
      license: 'PROPRIETARY',
      notice: 'Unauthorized removal of credits prohibited'
    };
    console.log('%c📚 Courses Module | Proprietary Software by Srivatsan Sk', 'color: #4CAF50; font-size: 12px; font-weight: bold;');
  }, []);

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                setLoading(true);
                const response = await api.courses.getAll();
                const coursesData = response || [];
                setCourses(coursesData);

                // Calculate stats
                const totalCourses = coursesData.length;
                const freeCourses = coursesData.filter(course => course.is_free).length;
                const paidCourses = totalCourses - freeCourses;
                const enrolledCourses = coursesData.filter(course => course.has_purchased || course.is_enrolled).length;

                setStats({
                    totalCourses,
                    freeCourses,
                    paidCourses,
                    enrolledCourses
                });

                setError(null);
            } catch (err) {
                console.error('Error fetching courses:', err);
                setError('Failed to load courses. Please try again later.');

              // Use centralized error handling
              handleApiError(err, 'Failed to load courses. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchCourses();
    }, []);

    const handleEnrollmentChange = () => {
        // Refresh courses data when enrollment changes
        window.location.reload();
    };

    return (
        <Layout>
            <Header>
                <Title>Cyber Security Courses</Title>
                <Subtitle>Browse our comprehensive collection of cyber security courses</Subtitle>
            </Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {loading ? (
                <LoadingIndicator>Loading courses...</LoadingIndicator>
            ) : (
                    <PageContainer>
                        <StatsContainer>
                            <StatCard>
                                <StatValue>{stats.totalCourses}</StatValue>
                                <StatLabel>Total Courses</StatLabel>
                            </StatCard>
                            <StatCard>
                                <StatValue>{stats.enrolledCourses}</StatValue>
                                <StatLabel>Enrolled</StatLabel>
                            </StatCard>
                            <StatCard>
                                <StatValue>{stats.freeCourses}</StatValue>
                                <StatLabel>Free Courses</StatLabel>
                            </StatCard>
                            <StatCard>
                                <StatValue>{stats.paidCourses}</StatValue>
                                <StatLabel>Premium Courses</StatLabel>
                            </StatCard>
                        </StatsContainer>

                        <CoursesGrid>
                            {courses.length > 0 ? (
                                courses.map(course => (
                                        <CourseCard
                                            key={course.id}
                                            course={course}
                                            onEnroll={handleEnrollmentChange}
                                        />
                                    ))
                            ) : (
                                <p>No courses available.</p>
                            )}
                        </CoursesGrid>
                    </PageContainer>
            )}
        </Layout>
    );
};

export default Courses;